import dateFormat from "dateformat";
import {DateFormatTypeEnum, HasCreationDateModel} from "../../models";
import {format, parseISO} from "date-fns";

type SortingMode = "asc" | "desc";
//#region public
export const addDays=(date: Date, days: number): Date => {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}
export const convertDateToLocalIsoDate = (date?: Date|null): Date|null => {
  if(!date)return null;
  try {
    const offset = date.getTimezoneOffset() * 60000; // Get local offset in milliseconds
    return new Date(date.getTime() - offset);
  }
  catch (error) {
    return date;
  }
};
export const convertDateToLocalIsoString = (date: Date): string|null => {
  if(!date)return null;
  const offset = date.getTimezoneOffset() * 60000; // Get local offset in milliseconds
  return new Date(date.getTime() - offset).toISOString().slice(0, -1);// Remove 'Z'
};
export const getLastDayTime = (inputDate: Date): Date => {
  const dateCopy = new Date(inputDate);
  dateCopy.setHours(23, 59, 59, 999); // Set the time to the last millisecond of the day
  return dateCopy;
};
export const getFirstDayTime = (inputDate: Date): Date => {
  const dateCopy = new Date(inputDate);
  dateCopy.setHours(0, 0, 0, 0); // Set the time to the first millisecond of the day
  return dateCopy;
};
export const formatToIsoString = (dateString: string | Date) => {
  if (!dateString) {
    return "";
  }
  const date =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  // First format to ISO without timezone
  const isoDate = format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
  // Then parse and return as proper ISO string
  return parseISO(isoDate).toISOString();
};
export const formatDate = (
  value: string | Date | null | undefined,
  formatter?: string | null
): string => {
  formatter =
    formatter === null || formatter === undefined || formatter === ""
      ? "dd/mm/yyyy"
      : formatter;
  return value === null || value === undefined || value === ""
    ? ""
    : dateFormat(value, formatter);
};
export const sortByDate = (
  array: HasCreationDateModel[],
  mode?: SortingMode
) => {
  return array.sort((a, b) => {
    const dateA = new Date(a.CreationDate);
    const dateB = new Date(b.CreationDate);

    if (mode === "desc") {
      return dateB.getTime() - dateA.getTime();
    } else {
      return dateA.getTime() - dateB.getTime();
    }
  });
};
export const formatDateValue = (date: Date | string | null | undefined, formatType: DateFormatTypeEnum = DateFormatTypeEnum.Default): string| null => {
  if (!date) return null;

  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (isNaN(parsedDate.getTime())) return null; // Invalid date handling
  switch (formatType) {
    case DateFormatTypeEnum.Iso_Date:
      return formatToIsoDate(parsedDate);
    case DateFormatTypeEnum.Iso_Date_Time:
      return formatToIsoDateTime(parsedDate);
    case DateFormatTypeEnum.Date_Only:
      return formatToDateOnly(parsedDate);
    case DateFormatTypeEnum.Time_Only:
      return formatToTimeOnly(parsedDate);
    case DateFormatTypeEnum.Original:
      return formatToOriginal(parsedDate);
    case DateFormatTypeEnum.Iso_String:
    case DateFormatTypeEnum.Default:
    default:
      //return formatToIsoStringV2(parsedDate);
      return formatToIsoDateTime(parsedDate);
  }
};
export const removeUtcOffset = (
    date: Date | string | null | undefined
): Date | null => {
  if (!date) return null; // Handle null/undefined

  let parsedDate = typeof date === "string" ? new Date(date) : date;
  if (isNaN(parsedDate.getTime())) return null; //Handle invalid date

  return new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      parsedDate.getHours(),
      parsedDate.getMinutes(),
      parsedDate.getSeconds()
  ); // Return local Date object without "Z"
};
//#endregion
//#region private
// Method 1: ISO_DATE (YYYY-MM-DDT00:00:00.000)
const formatToIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00.000`;
};

// Method 2: ISO_STRING (Full ISO format)
const formatToIsoStringV2 = (date: Date): string => {
  return date.toISOString(); // Converts to "YYYY-MM-DDTHH:mm:ss.sssZ"
};

// Method 3: ISO_DATE_TIME (YYYY-MM-DDTHH:mm:ss)
const formatToIsoDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

// Method 4: DATE_ONLY (YYYY-MM-DD)
const formatToDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Method 5: TIME_ONLY (HH:mm:ss)
const formatToTimeOnly = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

// Method 6: ORIGINAL (Returns the input date as a string)
const formatToOriginal = (date: Date): string => {
  return date.toString(); // Converts the input date to a standard string format
};
//#endregion